
// src/components/layout/LoggedInNavigation.js
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useAuth } from "../../AuthUser";
import { useAuthModal } from "../../features/Auth/useAuthModal";
import ProfileImage from "../ProfileImage";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, NavLink } from "react-router-dom";
import "../../assets/css/navigation-search.css";
import BottomNav from '../BottomNav';
import API from "../../api";

/** Tunables ***************************************************************/
export const CARET_SIZE = "0.6rem";                  // ▼ caret size for the Videos trigger
export const CATEGORY_CACHE_TTL_MS = 60 * 60 * 1000;  // 1 hour cache; change to 5*60*1000 for 5 minutes
const CATEGORY_CACHE_KEY = "videoCategories:v1";      // sessionStorage key
/***************************************************************************/

/** ------------------------------------------------------------------------
 *  VideoMenu (desktop)
 *  - Matches the account dropdown style (bn-dropup, bn-item, bn-sep)
 *  - Items order:
 *      1) New (bold)
 *      2) Popular (bold)
 *      3) Testimonials (bold, only if user is free/unauthenticated)
 *      --- separator ---
 *      4+) Categories fetched from /video/categories (cached per session)
 *  - Opens on hover. Clicking the trigger navigates to /videos.
 *  ---------------------------------------------------------------------- */
function VideoMenu() {
  const navigate = useNavigate();
  const auth = useAuth();
  const isFree = !auth?.user?.planType; // unauthenticated counts as free
  const [open, setOpen] = useState(false);
  const [hoverCloseTimer, setHoverCloseTimer] = useState(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 12 });

  // Categories state
  const [cats, setCats] = useState([]);
  const [catsLoaded, setCatsLoaded] = useState(false);

  // --- caching helpers (sessionStorage) ---
  const readCache = () => {
    try {
      const raw = sessionStorage.getItem(CATEGORY_CACHE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || typeof obj !== "object") return null;
      if (!obj.ts || !Array.isArray(obj.data)) return null;
      if (Date.now() - obj.ts > CATEGORY_CACHE_TTL_MS) return null; // expired
      return obj.data;
    } catch {
      return null;
    }
  };
  const writeCache = (data) => {
    try {
      sessionStorage.setItem(CATEGORY_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
    } catch {}
  };

  // Fetch categories from API with cache
  useEffect(() => {
    let alive = true;

    const fromCache = readCache();
    if (fromCache) {
      setCats(fromCache);
      setCatsLoaded(true);
    }

    (async () => {
      try {
        const res = await API.get("/video/categories");
        // Try common shapes safely
        const payload = res?.data?.data || res?.data || [];
        const mapped = Array.isArray(payload)
            ? payload
                .filter(Boolean)
                .map(c => ({
                  id: c.id ?? c.category_id ?? c.value ?? c.slug ?? String(c.name || c.label || "category"),
                  name: (c.name || c.label || c.title || "Category").toString(),
                  slug: (c.slug || "").toString()
                }))
                // Remove "Testimonials" from API so we can hardcode it in top section
                .filter(c => {
                  const n = (c.name || "").toLowerCase();
                  const s = (c.slug || "").toLowerCase();
                  return !["testimonials","testimonial"].includes(n) && !["testimonials","testimonial"].includes(s);
                })
            : [];

        if (alive) {
          setCats(mapped);
          setCatsLoaded(true);
          writeCache(mapped); // update cache
        }
      } catch (e) {
        if (alive && !fromCache) {
          setCats([]);
          setCatsLoaded(true);
        }
      }
    })();

    return () => { alive = false; };
  }, []);

  // position calc for the floating menu
  const recalc = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = Math.round(r.bottom + 8);
    const right = Math.round(window.innerWidth - r.right);
    setPos({ top, right });
  };

  useEffect(() => {
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // open on hover for the trigger; close on mouseleave of menu wrapper
  const onEnter = () => {
    if (hoverCloseTimer) { clearTimeout(hoverCloseTimer); }
    setOpen(true);
  };
  const onLeave = () => {
    const t = setTimeout(() => setOpen(false), 120);
    setHoverCloseTimer(t);
  };

  const go = (to) => {
    setOpen(false);
    if (to.startsWith('http')) {
      window.location.href = to;
      return;
    }
    if (to.startsWith('?')) {
      const params = new URLSearchParams(to.slice(1));
      const qs = params.toString();
      navigate(`/videos${qs ? `?${qs}` : ''}`);
      return;
    }
    navigate(to);
  };

  const catsToShow = catsLoaded ? cats : [];

  return (
      <div className="position-relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <button
            ref={btnRef}
            type="button"
            className="btn p-0 border-0 bg-transparent nav-link d-inline-flex align-items-center align-baseline"
            aria-label="Videos"
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : 'false'}
            onClick={() => navigate("/videos")}
            style={{ lineHeight: 1 }}
        >
          <span>Videos</span>
          <i className="bi bi-chevron-down ms-1" style={{ fontSize: CARET_SIZE }} />
        </button>

        {open && (
            <div
                className="bn-dropup"
                role="menu"
                style={{ position: 'fixed', top: pos.top, right: pos.right, bottom: 'auto', transformOrigin: 'top right' }}
            >
              {/* New */}
              <button className="bn-item fw-semibold" role="menuitem" onClick={() => go("/videos?sort=newest")}>
                <i className="bi bi-stars" />
                <span>New</span>
              </button>

              {/* Popular */}
              <button className="bn-item fw-semibold" role="menuitem" onClick={() => go("/videos?sort=popular")}>
                <i className="fa fa-arrow-trend-up" />
                <span>Popular</span>
              </button>

              {/* Testimonials (free only) */}
              {isFree && (
                  <button className="bn-item fw-semibold" role="menuitem" onClick={() => go("/videos?category=12")}>
                    <i className="bi bi-chat-quote" />
                    <span>Testimonials</span>
                  </button>
              )}

              {/* Separator before categories */}
              <div className="bn-sep" />

              {/* Categories from API */}
              {(!catsLoaded) && (
                  <div className="px-3 py-2 text-muted small" role="status">Loading…</div>
              )}
              {catsToShow.map((c) => (
                  <button
                      key={String(c.id)}
                      className="bn-item"
                      role="menuitem"
                      onClick={() => go(`/videos?category=${encodeURIComponent(c.id)}`)}
                  >
                    <span>{c.name}</span>
                  </button>
              ))}
            </div>
        )}
      </div>
  );
}

function AvatarMenu() {
  const navigate = useNavigate();
  const auth = useAuth();
  const isAdmin = !!auth?.user?.admin;
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 12 });

  const recalc = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const top = Math.round(r.bottom + 8);
    const right = Math.round(window.innerWidth - r.right);
    setPos({ top, right });
  };

  useEffect(() => {
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to === '#signout') {
      if (auth.signout) auth.signout(() => navigate('/videos', { replace: true }));
      else if (auth.signOut) auth.signOut();
      return;
    }
    if (to.startsWith('http')) {
      window.location.href = to;
    } else {
      navigate(to);
    }
  };

  return (
      <div className="position-relative">
        <button
            ref={btnRef}
            type="button"
            className="btn p-0 border-0 bg-transparent"
            aria-label="Account menu"
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : 'false'}
            onClick={() => setOpen(v => !v)}
        >
          <ProfileImage size={40} textSizeRatio={2.75} text={auth.user?.email || ""} />
        </button>

        {open && (
            <div
                className="bn-dropup"
                role="menu"
                style={{ position: 'fixed', top: pos.top, right: pos.right, bottom: 'auto', transformOrigin: 'top right' }}
            >
              <button className="bn-item" role="menuitem" onClick={() => go("/support")}>
                <i className="bi bi-life-preserver" />
                <span>Support</span>
              </button>

              {auth.user && (
                  <button className="bn-item" role="menuitem" onClick={() => go("/account/")}>
                    <i className="bi bi-gear" />
                    <span>Settings</span>
                  </button>
              )}

              {isAdmin && (
                  <button className="bn-item" role="menuitem" onClick={() => go("/admin/lessons/")}>
                    <i className="bi bi-speedometer2"></i>
                    <span>Admin Dashboard</span>
                  </button>
              )}

              <div className="bn-sep" />
              <button className="bn-item" role="menuitem" onClick={() => go("https://blog.mygolfdna.com")}>
                <i className="bi bi-journal-text" />
                <span>Blog</span>
              </button>
              <button className="bn-item" role="menuitem" onClick={() => go("https://shop.mygolfdna.com")}>
                <i className="bi bi-bag" />
                <span>Training Aids</span>
              </button>

              <div className="bn-sep" />
              {auth.user ? (
                  <button className="bn-item" role="menuitem" onClick={() => go("#signout")}>
                    <i className="bi bi-box-arrow-right" />
                    <span>Sign Out</span>
                  </button>
              ) : (
                  <button className="bn-item" role="menuitem" onClick={() => go("#signin")}>
                    <i className="bi bi-box-arrow-in-right" />
                    <span>Sign In</span>
                  </button>
              )}
            </div>
        )}
      </div>
  );
}

/** Mobile-only menu for guests: they have no BottomNav (that's members-only)
 *  and previously had no navigation at all on phones — just the logo. */
function GuestMenu() {
  const navigate = useNavigate();
  const { openAuthModal } = useAuthModal();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, right: 12 });

  const recalc = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({ top: Math.round(r.bottom + 8), right: Math.round(window.innerWidth - r.right) });
  };

  useEffect(() => {
    recalc();
    const onResize = () => recalc();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const go = (to) => {
    setOpen(false);
    if (to === '#signin') {
      openAuthModal({ mode: 'signin', closable: true });
      return;
    }
    if (to.startsWith('http')) {
      window.location.href = to;
      return;
    }
    navigate(to);
  };

  return (
      <div className="position-relative">
        <button
            ref={btnRef}
            type="button"
            className="icon-btn"
            aria-label="Open menu"
            aria-haspopup="menu"
            aria-expanded={open ? 'true' : 'false'}
            onClick={() => setOpen(v => !v)}
            style={{ background: 'transparent', border: 0, color: '#fff', fontSize: 24 }}
        >
          <i className={`bi ${open ? 'bi-x-lg' : 'bi-list'}`}></i>
        </button>

        {open && (
            <div
                className="bn-dropup"
                role="menu"
                style={{ position: 'fixed', top: pos.top, right: pos.right, bottom: 'auto', transformOrigin: 'top right' }}
            >
              <button className="bn-item" role="menuitem" onClick={() => go("/videos")}>
                <i className="bi bi-collection-play" />
                <span>Videos</span>
              </button>
              <button className="bn-item" role="menuitem" onClick={() => go("/reviews")}>
                <i className="bi bi-camera-video" />
                <span>Swing Reviews</span>
              </button>
              <button className="bn-item fw-semibold" role="menuitem" onClick={() => go("/pricing")}>
                <i className="fa fa-lock-open text-secondary" />
                <span className="text-secondary">Upgrade to Pro</span>
              </button>
              <button className="bn-item" role="menuitem" onClick={() => go("/free")}>
                <i className="bi bi-gift" />
                <span>Free Swing Review</span>
              </button>

              <div className="bn-sep" />
              <button className="bn-item" role="menuitem" onClick={() => go("https://blog.mygolfdna.com")}>
                <i className="bi bi-journal-text" />
                <span>Blog</span>
              </button>
              <button className="bn-item" role="menuitem" onClick={() => go("https://shop.mygolfdna.com")}>
                <i className="bi bi-bag" />
                <span>Training Aids</span>
              </button>

              <div className="bn-sep" />
              <button className="bn-item" role="menuitem" onClick={() => go("#signin")}>
                <i className="bi bi-box-arrow-in-right" />
                <span>Member Login</span>
              </button>
            </div>
        )}
      </div>
  );
}

function LoggedInNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const { openAuthModal } = useAuthModal();
  const isLoggedIn = !!auth?.user;

  // --- search overlay ---
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  const openSearch = () => {
    try {
      const params = new URLSearchParams(location.search);
      const q = params.get('q') || '';
      setSearchValue(q);
    } catch {}
    setShowSearch(true);
  };
  const closeSearch = () => setShowSearch(false);

  const submitSearch = () => {
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    const q = (searchValue || '').trim();
    const isOnVideos = location.pathname === '/videos';
    const params = new URLSearchParams(isOnVideos ? location.search : '');
    if (q) params.set('q', q); else params.delete('q');
    params.delete('category');
    const qs = params.toString();
    if (!isOnVideos) navigate(`/videos${qs ? `?${qs}` : ''}`, { replace: false });
    else navigate(`/videos${qs ? `?${qs}` : ''}`, { replace: true });
  };

  const clearSearch = () => {
    setSearchValue('');
    if (searchInputRef.current) searchInputRef.current.blur();
    const isOnVideos = location.pathname === '/videos';
    const params = new URLSearchParams(isOnVideos ? location.search : '');
    params.delete('q');
    params.delete('category');
    const qs = params.toString();
    if (!isOnVideos) {
      navigate(`/videos${qs ? `?${qs}` : ''}`, { replace: false });
    } else {
      navigate(`/videos${qs ? `?${qs}` : ''}`, { replace: true });
    }
  };

  // Back button logic (restored)
  const showBack = (location.pathname !== "/" && (location.pathname !== "/videos" ? true : !!location.search));

  // fixed-top spacer measures
  const navRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);
  const measure = () => { if (navRef.current) setNavHeight(navRef.current.offsetHeight || 0); };
  useLayoutEffect(measure, []);
  useEffect(() => { window.addEventListener('resize', measure); return () => window.removeEventListener('resize', measure); }, []);

  return (
      <>
        <Navbar ref={navRef} id="site-navbar" variant="dark" className="fixed-top bg-dark-blue" expand="lg">
          <Container className="position-relative">

            {/* SEARCH OVERLAY */}
            <div className={`nav-search-overlay ${showSearch ? 'open' : ''}`} role="dialog" aria-modal="true" aria-label="Search videos">
              <button
                  type="button"
                  className="ns-btn back"
                  aria-label="Close search"
                  onClick={closeSearch}
                  style={{ background: 'transparent', border: 0, color: '#cfd8e3' }}
              >
                <i className="bi bi-arrow-left"></i>
              </button>

              <Form className="ns-form" onSubmit={(e)=>{e.preventDefault(); submitSearch();}}>
                <div className="ns-input-wrap" style={{ position: 'relative', width: '100%' }}>
                  <Form.Control
                      ref={searchInputRef}
                      type="text"
                      inputMode="search"
                      placeholder="Search videos…"
                      value={searchValue}
                      onChange={(e)=> setSearchValue(e.target.value)}
                      aria-label="Search videos"
                      className="ns-input"
                      style={{ paddingRight: '36px' }}
                  />
                  {searchValue ? (
                      <button
                          type="button"
                          className="ns-clear"
                          aria-label="Clear search"
                          onClick={clearSearch}
                          style={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            border: 0,
                            background: 'transparent',
                            color: '#9aa3af',
                            fontSize: 20,
                            lineHeight: 1,
                            padding: 0,
                            cursor: 'pointer'
                          }}
                      >
                        &times;
                      </button>
                  ) : null}
                </div>
              </Form>

              <button
                  type="button"
                  className="ns-btn go"
                  aria-label="Search"
                  onClick={submitSearch}
                  style={{ background: 'transparent', border: 0, color: '#cfd8e3' }}
              >
                <i className="bi bi-search"></i>
              </button>
            </div>

            {/* DESKTOP HEADER (with Back button restored) */}
            <div className="d-none d-lg-flex w-100 align-items-center">
              <div className="d-flex align-items-center me-auto">
                {showBack && (
                    <button
                        type="button"
                        onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/videos"); }}
                        className="icon-btn me-2"
                        aria-label="Go back"
                        style={{ background: 'transparent', border: 0, color: '#cfd8e3', fontSize: '20px' }}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                )}
                <Navbar.Brand href="/videos" style={{ letterSpacing: '1px', marginTop: '5px'}} className="monument-font">
                  MYGOLF<span className="text-secondary">DNA</span>
                </Navbar.Brand>
              </div>

              <Navbar.Collapse id="basic-navbar-nav" className="d-flex">
                <Nav className="main-nav w-100 justify-content-center text-center align-items-center">
                  <VideoMenu />
                  <Nav.Link as={NavLink} to="/reviews">Swing Reviews</Nav.Link>
                  {auth?.user?.planType ? (
                      <>
                        <Nav.Link as={NavLink} to="/clinics/dual-force-golf-school" className="d-none d-xl-inline visually-hidden">
                          Clinics
                        </Nav.Link>
                        <Nav.Link href="https://meetings.hubspot.com/chris3535/mygolfdna">
                          PocketPRO
                        </Nav.Link>
                      </>
                  ) : (
                      <Nav.Link as={NavLink} to="/pricing" className="d-none d-lg-inline fw-semibold">
                        <i className="fa fa-lock-open me-1 text-secondary" aria-hidden="true"></i>
                        <span className="text-secondary">Upgrade to Pro</span>
                      </Nav.Link>
                  )}
                </Nav>

                <Nav className="d-flex align-items-center ms-lg-3 gap-2">
                  <button
                      type="button"
                      className="icon-btn"
                      aria-label="Open search"
                      onClick={openSearch}
                      style={{ background: 'transparent', border: 0 }}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                  {auth?.user && (
                      <Nav.Link
                          as={NavLink}
                          to="/upload-videos"
                          className="bn-item fw-semibold d-inline-flex align-items-center justify-content-center bn-cta bn-cta-wide text-decoration-none"
                          style={{
                            borderRadius: 14,
                            padding: "8px 14px",
                            background: "rgba(255,255,255,0.08)",
                            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)"
                          }}
                      >
                        <i className="bi bi-upload" aria-hidden="true" />
                        Submit Swing
                      </Nav.Link>
                  )}
                  {auth?.user ? (
                      <AvatarMenu />
                  ) : (
                      <Button
                          as={NavLink}
                          onClick={()=> openAuthModal({mode: 'signin', closable: true})}
                          variant="outline-secondary"
                          className="text-nowrap px-3"
                          style={{ whiteSpace: 'nowrap' }}
                      >
                        Member Login
                      </Button>
                  )}
                </Nav>
              </Navbar.Collapse>
            </div>

            {/* MOBILE HEADER (with Back button restored) */}
            <div className="d-lg-none d-flex align-items-center w-100">
              <div className="d-flex align-items-center me-auto">
                {showBack && (
                    <button
                        type="button"
                        onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/videos"); }}
                        className="icon-btn me-2"
                        aria-label="Go back"
                        style={{ background: 'transparent', border: 0, color: '#cfd8e3', fontSize: '20px' }}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                )}
                <a
                    href="/videos"
                    className="navbar-brand-small text-decoration-none monument-font"
                    style={{ letterSpacing: '1px', marginTop: '5px' }}
                >
                  <span className="text-white">MYGOLF</span><span className="text-secondary">DNA</span>
                </a>
              </div>

              <div className="d-flex align-items-center gap-2">
                {/* Members: search only (BottomNav covers navigation).
                    Guests: hamburger menu instead — search targets member
                    content and guests previously had no navigation at all. */}
                {isLoggedIn ? (
                    <button type="button" className="icon-btn" aria-label="Open search" onClick={openSearch} style={{ background: 'transparent', border: 0 }}>
                      <i className="bi bi-search"></i>
                    </button>
                ) : (
                    <GuestMenu />
                )}
              </div>
            </div>
          </Container>
        </Navbar>

        {/* Spacer to offset fixed-top height */}
        <div style={{ height: navHeight }} />
        {/* Mobile bottom nav (unchanged) */}
        {isLoggedIn && <BottomNav />}
      </>
  );
}

export default LoggedInNavigation;
